// Panneau latéral de saisie.
//
// Accessibilité portée ici une fois pour toutes, parce qu'une modale mal faite
// est un piège au clavier : `<dialog>` natif (le navigateur retient le focus
// dans le panneau et rend le reste de la page inerte), fermeture par Échap,
// focus déplacé dans le panneau à l'ouverture et RENDU au déclencheur à la
// fermeture, et défilement de l'arrière-plan bloqué.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import './write.css';

interface DrawerProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  /** Panneau large : pour les éditeurs qui ont besoin de place (protocole d'étude). */
  wide?: boolean;
  children: ReactNode;
}

export function Drawer({ open, title, description, onClose, wide = false, children }: DrawerProps) {
  const panelRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    // Mémorise le déclencheur pour lui rendre le focus à la fermeture : sans
    // cela, l'utilisateur au clavier repart du haut de la page.
    returnFocusRef.current = document.activeElement as HTMLElement | null;

    const dialog = panelRef.current;
    if (dialog && !dialog.open) dialog.showModal();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Premier champ utile, sinon le panneau lui-même.
    const first = panelRef.current?.querySelector<HTMLElement>(
      'input, textarea, select, button',
    );
    (first ?? panelRef.current)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <dialog
      className={`zy-drawer${wide ? ' drawer-wide' : ''}`}
      aria-labelledby={titleId}
      tabIndex={-1}
      ref={panelRef}
      // Échap : le navigateur fermerait seul le dialogue, l'état resterait ouvert.
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // Un clic sur le voile (le dialogue lui-même, sans marge) referme le panneau.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="zy-panneau">
        <header className="zy-panneau__tete">
          <div>
            <h2 className="t-h2" id={titleId}>
              {title}
            </h2>
            {description ? <p className="zy-texte-muet">{description}</p> : null}
          </div>
          <button type="button" className="zy-icon-btn drawer-close" onClick={onClose} aria-label="Fermer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="zy-panneau__corps">{children}</div>
      </div>
    </dialog>
  );
}
